import {
  BookOpen,
  CalendarPlus,
  ChevronRight,
  CreditCard,
  Gift,
  Heart,
  PawPrint,
  Plus,
  Receipt,
  Repeat,
  ShieldPlus,
  Sparkles,
  User,
  Users,
} from 'lucide-react';
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import type { BookingSummary } from '../components/BookingRow';
import { BookingRow } from '../components/BookingRow';
import type { PetSummary } from '../components/PetCard';
import { PushPermissionPrompt } from '../components/PushPermissionPrompt';
import { customerApi } from '../customer_api';
import { bumpInstallVisit } from '../pwa';
import { useCustomerAuth } from '../useCustomerAuth';

function QuickLink({
  to,
  icon,
  label,
}: {
  to: string;
  icon: React.ReactElement;
  label: string;
}) {
  return (
    <Link
      to={to}
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        minHeight: '44px',
        padding: '0.85rem 1rem',
        borderRadius: '10px',
        border: '1px solid var(--border, oklch(0.915 0.005 80))',
        background: 'var(--surface, white)',
        textDecoration: 'none',
        color: 'var(--foreground, oklch(0.19 0.012 75))',
        fontSize: '0.9rem',
        fontWeight: 500,
      }}
    >
      <span style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
        <span style={{ color: 'var(--primary, oklch(0.43 0.18 263))' }}>{icon}</span>
        {label}
      </span>
      <ChevronRight size={15} style={{ color: 'var(--muted-foreground, oklch(0.52 0.008 75))' }} />
    </Link>
  );
}

function SectionCard({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div
      style={{
        marginTop: '1.5rem',
        padding: '1.25rem',
        background: 'var(--surface, white)',
        borderRadius: '12px',
        border: '1px solid var(--border, oklch(0.915 0.005 80))',
      }}
    >
      <div
        style={{
          fontSize: '0.7rem',
          textTransform: 'uppercase',
          letterSpacing: '0.14em',
          color: 'var(--muted-foreground, oklch(0.52 0.008 75))',
          marginBottom: '0.75rem',
        }}
      >
        {title}
      </div>
      {children}
    </div>
  );
}

import type React from 'react';

interface LoyaltyBalance {
  balance: number;
  programName?: string | null;
}
interface ReferralCode {
  code?: string | null;
}

function daysUntil(iso: string): number | null {
  const target = new Date(iso).getTime();
  if (Number.isNaN(target)) return null;
  const ms = target - Date.now();
  if (ms < 0) return null;
  return Math.ceil(ms / (24 * 60 * 60 * 1000));
}

function bookingStartIso(b: BookingSummary): string | null {
  // BookingSummary is intentionally narrow — we use createdAt as a proxy for
  // "next stay date" when the API doesn't expose a typed startAt yet. The
  // detail fetch on /account/bookings/:id has the authoritative date.
  const r = b as unknown as { startAt?: string; checkIn?: string; createdAt?: string };
  return r.startAt ?? r.checkIn ?? r.createdAt ?? null;
}

export function AccountHome() {
  const { user } = useCustomerAuth();
  const [bookings, setBookings] = useState<BookingSummary[] | null>(null);
  const [pets, setPets] = useState<PetSummary[] | null>(null);
  const [loyalty, setLoyalty] = useState<LoyaltyBalance | null>(null);
  const [referral, setReferral] = useState<ReferralCode | null>(null);

  useEffect(() => {
    // PWA: bump the per-customer visit counter (used by InstallPrompt to
    // gate the "Install kennel app" banner after 2 visits).
    bumpInstallVisit();

    customerApi<{ bookings: BookingSummary[] }>('/api/customer/bookings')
      .then((d) => setBookings(d.bookings))
      .catch(() => setBookings([]));

    customerApi<{ pets: PetSummary[] }>('/api/customer/pets')
      .then((d) => setPets(d.pets))
      .catch(() => setPets([]));

    customerApi<LoyaltyBalance>('/api/customer/loyalty/balance')
      .then(setLoyalty)
      .catch(() => setLoyalty(null));

    customerApi<ReferralCode>('/api/customer/referrals/code')
      .then(setReferral)
      .catch(() => setReferral(null));
  }, []);

  if (!user) return null;

  const firstName = user.name ? user.name.split(' ')[0] : null;

  const activeStays = (bookings ?? []).filter((b) => b.status === 'checked_in');
  const upcoming = (bookings ?? [])
    .filter(
      (b) =>
        b.status !== 'checked_in' &&
        b.status !== 'checked_out' &&
        b.status !== 'cancelled' &&
        b.status !== 'no_show',
    )
    .slice(0, 5);

  // "Your next stay in X days" countdown — use the earliest upcoming booking.
  const nextStayDays = (() => {
    if (upcoming.length === 0) return null;
    const candidates = upcoming
      .map((b) => bookingStartIso(b))
      .filter((s): s is string => typeof s === 'string')
      .map((s) => daysUntil(s))
      .filter((d): d is number => d !== null);
    return candidates.length ? Math.min(...candidates) : null;
  })();

  const hasUpcoming = upcoming.length > 0;
  const hasRecent = (bookings ?? []).some((b) => b.status === 'checked_out');

  return (
    <main className="min-h-[100dvh] bg-background px-4 py-6 sm:px-6 sm:py-10">
      <div className="mx-auto max-w-2xl">
        <h1
          style={{
            fontSize: '2.25rem',
            fontWeight: 500,
            letterSpacing: '-0.02em',
            margin: '0.4rem 0 0.25rem',
          }}
        >
          Hi{firstName ? `, ${firstName}` : ''}
        </h1>
        <p
          style={{
            margin: '0 0 0.5rem',
            color: 'var(--muted-foreground, oklch(0.52 0.008 75))',
            fontSize: '0.875rem',
          }}
        >
          {user.email}
        </p>

        {/* Primary CTAs — Book a stay + Add a pet */}
        <div
          style={{
            marginTop: '1.5rem',
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: '0.75rem',
          }}
        >
          <Link
            to="/book"
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '0.5rem',
              minHeight: '44px',
              padding: '0.85rem 1rem',
              borderRadius: '10px',
              background: 'var(--primary, oklch(0.43 0.18 263))',
              color: 'white',
              textDecoration: 'none',
              fontSize: '0.9rem',
              fontWeight: 500,
            }}
          >
            <CalendarPlus size={16} />
            Book a new stay
          </Link>
          <Link
            to="/account/pets"
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '0.5rem',
              minHeight: '44px',
              padding: '0.85rem 1rem',
              borderRadius: '10px',
              border: '1px solid var(--border, oklch(0.915 0.005 80))',
              background: 'var(--surface, white)',
              color: 'var(--foreground, oklch(0.19 0.012 75))',
              textDecoration: 'none',
              fontSize: '0.9rem',
              fontWeight: 500,
            }}
          >
            <Plus size={16} />
            Manage pets
          </Link>
        </div>

        {/* Next-stay countdown — only shown when an upcoming booking exists. */}
        {nextStayDays !== null && (
          <div
            data-testid="next-stay-countdown"
            style={{
              marginTop: '1.5rem',
              padding: '1.25rem',
              borderRadius: '12px',
              border: '1px solid var(--border, oklch(0.915 0.005 80))',
              background:
                'linear-gradient(135deg, oklch(0.43 0.18 263 / 0.08), oklch(0.43 0.18 263 / 0.02))',
              display: 'flex',
              alignItems: 'center',
              gap: '0.85rem',
            }}
          >
            <span
              style={{
                width: '2.4rem',
                height: '2.4rem',
                borderRadius: '8px',
                background: 'var(--primary, oklch(0.43 0.18 263))',
                color: 'white',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Sparkles size={18} />
            </span>
            <div style={{ flex: 1 }}>
              <div
                style={{
                  fontSize: '0.7rem',
                  textTransform: 'uppercase',
                  letterSpacing: '0.14em',
                  color: 'var(--muted-foreground, oklch(0.52 0.008 75))',
                }}
              >
                Your next stay
              </div>
              <div style={{ fontSize: '1.05rem', fontWeight: 500 }}>
                <span className="num">{nextStayDays}</span> {nextStayDays === 1 ? 'day' : 'days'}{' '}
                away
              </div>
            </div>
          </div>
        )}

        {/* Loyalty balance — only shown when the program is on and the
            customer has a balance > 0. */}
        {loyalty && loyalty.balance > 0 && (
          <Link
            to="/account/rewards"
            data-testid="loyalty-balance-card"
            style={{
              marginTop: '1rem',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '1rem 1.25rem',
              borderRadius: '12px',
              border: '1px solid var(--border, oklch(0.915 0.005 80))',
              background: 'var(--surface, white)',
              textDecoration: 'none',
              color: 'inherit',
            }}
          >
            <span style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
              <Heart size={18} style={{ color: 'oklch(0.55 0.18 25)' }} />
              <span>
                <div style={{ fontSize: '0.75rem', opacity: 0.65 }}>
                  {loyalty.programName ?? 'Loyalty'}
                </div>
                <div style={{ fontWeight: 500 }}>
                  <span className="num">{loyalty.balance}</span> points
                </div>
              </span>
            </span>
            <span style={{ fontSize: '0.85rem', color: 'var(--primary, oklch(0.43 0.18 263))' }}>
              Redeem →
            </span>
          </Link>
        )}

        {/* "Bring back a friend" — shown when there's NO upcoming booking and
            the customer has a recent past visit. */}
        {!hasUpcoming && hasRecent && (
          <Link
            to="/book"
            data-testid="rebook-prompt-card"
            style={{
              marginTop: '1rem',
              display: 'block',
              padding: '1.25rem',
              borderRadius: '12px',
              border: '1px solid var(--border, oklch(0.915 0.005 80))',
              background: 'var(--surface, white)',
              textDecoration: 'none',
              color: 'inherit',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
              <PawPrint size={18} style={{ color: 'var(--primary, oklch(0.43 0.18 263))' }} />
              <div>
                <div style={{ fontWeight: 500 }}>Time to bring them back?</div>
                <div style={{ fontSize: '0.85rem', opacity: 0.7, marginTop: '0.15rem' }}>
                  Book a new stay in seconds — we already know your pet's preferences.
                </div>
              </div>
            </div>
          </Link>
        )}

        {/* Referral share card — shown when a code exists. */}
        {referral?.code && (
          <div
            data-testid="referral-card"
            style={{
              marginTop: '1rem',
              padding: '1.25rem',
              borderRadius: '12px',
              border: '1px solid var(--border, oklch(0.915 0.005 80))',
              background: 'var(--surface, white)',
              display: 'flex',
              alignItems: 'center',
              gap: '0.85rem',
            }}
          >
            <Gift size={18} style={{ color: 'oklch(0.55 0.15 150)' }} />
            <div style={{ flex: 1 }}>
              <div
                style={{
                  fontSize: '0.7rem',
                  textTransform: 'uppercase',
                  letterSpacing: '0.14em',
                  color: 'var(--muted-foreground, oklch(0.52 0.008 75))',
                }}
              >
                Refer a friend
              </div>
              <div style={{ fontSize: '0.95rem', marginTop: '0.15rem' }}>
                Share <code style={{ fontWeight: 500 }}>{referral.code}</code> — they get a
                discount, you get rewarded.
              </div>
            </div>
            <button
              type="button"
              onClick={() => {
                const shareUrl = `${window.location.origin}/?ref=${referral.code}`;
                if (navigator.share) {
                  navigator
                    .share({ title: 'Try this kennel', text: 'Use my code:', url: shareUrl })
                    .catch(() => {
                      navigator.clipboard?.writeText(shareUrl).catch(() => {});
                    });
                } else {
                  navigator.clipboard?.writeText(shareUrl).catch(() => {});
                }
              }}
              style={{
                padding: '0.45rem 0.85rem',
                borderRadius: '8px',
                background: 'var(--primary, oklch(0.43 0.18 263))',
                color: 'white',
                border: 'none',
                fontSize: '0.8rem',
                cursor: 'pointer',
              }}
            >
              Share
            </button>
          </div>
        )}

        {/* PWA: gentle in-app prompt to enable push notifications. Only
            renders for browsers that support push + permission='default'. */}
        <div style={{ marginTop: '1.5rem' }}>
          <PushPermissionPrompt context="home" />
        </div>

        {/* Active stays */}
        <SectionCard title="Active stays">
          {bookings === null ? (
            <p
              style={{
                margin: 0,
                fontSize: '0.875rem',
                color: 'var(--muted-foreground, oklch(0.52 0.008 75))',
              }}
            >
              Loading…
            </p>
          ) : activeStays.length === 0 ? (
            <p
              style={{
                margin: 0,
                fontSize: '0.875rem',
                color: 'var(--muted-foreground, oklch(0.52 0.008 75))',
              }}
            >
              No active stays.
            </p>
          ) : (
            <div style={{ borderTop: '1px solid var(--border, oklch(0.915 0.005 80))' }}>
              {activeStays.map((b) => (
                <div
                  key={b.id}
                  style={{ borderBottom: '1px solid var(--border, oklch(0.915 0.005 80))' }}
                >
                  <BookingRow booking={b} />
                </div>
              ))}
            </div>
          )}
        </SectionCard>

        {/* Upcoming */}
        <SectionCard title="Upcoming">
          {bookings === null ? (
            <p
              style={{
                margin: 0,
                fontSize: '0.875rem',
                color: 'var(--muted-foreground, oklch(0.52 0.008 75))',
              }}
            >
              Loading…
            </p>
          ) : upcoming.length === 0 ? (
            <p
              style={{
                margin: 0,
                fontSize: '0.875rem',
                color: 'var(--muted-foreground, oklch(0.52 0.008 75))',
              }}
            >
              Nothing upcoming.
            </p>
          ) : (
            <div style={{ borderTop: '1px solid var(--border, oklch(0.915 0.005 80))' }}>
              {upcoming.map((b) => (
                <div
                  key={b.id}
                  style={{ borderBottom: '1px solid var(--border, oklch(0.915 0.005 80))' }}
                >
                  <BookingRow booking={b} />
                </div>
              ))}
            </div>
          )}
        </SectionCard>

        {/* Pets strip */}
        {pets !== null && pets.length > 0 && (
          <SectionCard title="Your pets">
            <div
              style={{
                display: 'flex',
                gap: '0.75rem',
                overflowX: 'auto',
                paddingBottom: '0.25rem',
              }}
            >
              {pets.map((p) => (
                <Link
                  key={p.id}
                  to={`/account/pets/${p.id}`}
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: '0.35rem',
                    textDecoration: 'none',
                    color: 'inherit',
                    flexShrink: 0,
                  }}
                >
                  <div
                    style={{
                      width: '3rem',
                      height: '3rem',
                      borderRadius: '8px',
                      overflow: 'hidden',
                      background: 'oklch(0.94 0.005 80)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    {p.photoUrl ? (
                      <img
                        src={p.photoUrl}
                        alt={p.name}
                        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                      />
                    ) : (
                      <PawPrint
                        size={18}
                        style={{ color: 'var(--muted-foreground, oklch(0.52 0.008 75))' }}
                      />
                    )}
                  </div>
                  <span style={{ fontSize: '0.72rem', fontWeight: 500 }}>{p.name}</span>
                </Link>
              ))}
            </div>
          </SectionCard>
        )}

        {/* Quick links */}
        <div
          style={{ marginTop: '1.75rem', display: 'flex', flexDirection: 'column', gap: '0.6rem' }}
        >
          <QuickLink to="/account/bookings" icon={<BookOpen size={16} />} label="Bookings" />
          <QuickLink to="/account/payments" icon={<Receipt size={16} />} label="Payments" />
          <QuickLink
            to="/account/payment-methods"
            icon={<CreditCard size={16} />}
            label="Saved cards"
          />
          <QuickLink to="/account/credits" icon={<Gift size={16} />} label="Credits" />
          <QuickLink to="/account/packs" icon={<Repeat size={16} />} label="Packs & memberships" />
          <QuickLink to="/account/pets" icon={<PawPrint size={16} />} label="Pets" />
          <QuickLink to="/account/profile" icon={<User size={16} />} label="Profile" />
          <QuickLink to="/account/household" icon={<Users size={16} />} label="Household" />
          <QuickLink
            to="/account/notifications"
            icon={<Sparkles size={16} />}
            label="Notification preferences"
          />
          <QuickLink to="/account/insurance" icon={<ShieldPlus size={16} />} label="Insurance" />
          <QuickLink to="/account/privacy" icon={<User size={16} />} label="Privacy & data" />
        </div>
      </div>
    </main>
  );
}
